<template>
  <div>
    <HrPageLayout
      title="Manage Documents"
      subtitle="Upload, edit and manage organization documents"
      icon="📄"
      :breadcrumb-items="[
        { label: 'Home', route: '/dashboard' },
        { label: 'Documents' }
      ]"
    >
      <template #header-actions>
        <button type="button" class="hr-btn hr-btn-primary" @click="exportReport">
          <i class="bx bxs-download"></i> Download Report
        </button>
        <button type="button" class="hr-btn hr-btn-success" @click="openAddModal">
          <i class="bx bx-plus"></i> Add Document
        </button>
      </template>

      <div class="documents-general-content">
        <datalist id="document-type-datalist">
          <option v-for="t in documentTypeSuggestions" :key="'dt-' + t" :value="t"></option>
        </datalist>
        <div v-if="message" class="doc-toast" :class="toastKind">
          <strong>{{ toastHeading }}</strong>
          <span>{{ message }}</span>
          <button type="button" class="doc-toast-close" @click="clearToast">&times;</button>
        </div>

        <!-- Stats cards -->
        <div v-if="statisticsEnabled" class="hr-metrics-grid hr-mb-4">
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-icon"><i class="bx bxs-file"></i></div>
            <p class="hr-metric-label">Total Documents</p>
            <h2 class="hr-metric-value">{{ statistics.totalDocuments }}</h2>
          </div>
          <div class="hr-metric-card gradient-2">
            <div class="hr-metric-icon"><i class="bx bx-upload"></i></div>
            <p class="hr-metric-label">Recent Uploads</p>
            <h2 class="hr-metric-value">{{ statistics.recentUploads }}</h2>
          </div>
          <div class="hr-metric-card gradient-3">
            <div class="hr-metric-icon"><i class="bx bx-category"></i></div>
            <p class="hr-metric-label">Categories</p>
            <h2 class="hr-metric-value">{{ statistics.categories }}</h2>
          </div>
          <div class="hr-metric-card gradient-4">
            <div class="hr-metric-icon"><i class="bx bx-building"></i></div>
            <p class="hr-metric-label">Departments</p>
            <h2 class="hr-metric-value">{{ departments.length }}</h2>
          </div>
        </div>

        <!-- Filters and table -->
        <div class="hr-chart-card">
          <div class="hr-chart-header hr-flex-between">
            <div class="doc-filters">
              <div class="hr-search-wrap">
                <input v-model="searchKeyword" @input="handleSearch" class="hr-input" type="text"
                       placeholder="Search documents..." spellcheck="false">
                <i class="bx bx-search-alt search-icon"></i>
              </div>
              <div class="hr-filter-group">
                <label class="hr-form-label">Department</label>
                <select v-model="selectedDepartment" @change="handleDepartmentChange" class="hr-select" style="min-width: 180px;">
                  <option value="">All Departments</option>
                  <option v-for="department in departments" :key="department.id" :value="department.id">
                    {{ department.departmentName }}
                  </option>
                </select>
              </div>
              <div class="hr-filter-group">
                <label class="hr-form-label">Document type</label>
                <input
                  v-model="selectedDocumentType"
                  class="hr-input"
                  style="min-width: 180px;"
                  list="document-type-datalist"
                  placeholder="Filter by type…"
                  @change="handleDocumentTypeFilterChange"
                  @keyup.enter="handleDocumentTypeFilterChange"
                >
              </div>
            </div>
          </div>
          <div class="hr-chart-body">
            <div v-if="isLoading" class="hr-loading-wrap">
              <div class="hr-spinner"></div>
              <p class="hr-text-muted hr-mt-2">Loading documents...</p>
            </div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover hr-table-striped">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Document Name</th>
                    <th>Description</th>
                    <th>Department</th>
                    <th>Category</th>
                    <th>Document type</th>
                    <th>Upload Date</th>
                    <th>Views</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(document, index) in documents" :key="document.id">
                    <td>{{ index + 1 }}</td>
                    <td>{{ document.name }}</td>
                    <td class="doc-desc-cell">{{ document.description || '—' }}</td>
                    <td>{{ document.departmentName || 'General' }}</td>
                    <td>
                      <span class="hr-badge" :class="getCategoryBadgeClass(document.category)">
                        {{ document.document_type || document.documentType || document.category || 'Uncategorized' }}
                      </span>
                    </td>
                    <td>{{ document.document_type || document.documentType || '—' }}</td>
                    <td>{{ formatDate(document.createdAt) }}</td>
                    <td><span class="hr-badge hr-badge-info">{{ document.views || 0 }}</span></td>
                    <td>
                      <div class="hr-btn-group doc-actions">
                        <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary" title="Download" @click="downloadDocument(document)">
                          <i class="bx bx-download"></i>
                        </button>
                        <button type="button" class="hr-btn hr-btn-sm hr-btn-info" title="Email to employee" @click="openSendEmailModal(document)">
                          <i class="bx bx-mail-send"></i>
                        </button>
                        <button type="button" class="hr-btn hr-btn-sm hr-btn-warning" title="Edit" @click="editDocument(document)">
                          <i class="bx bx-edit"></i>
                        </button>
                        <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" title="View" @click="viewDocument(document)">
                          <i class="bx bx-show"></i>
                        </button>
                        <button type="button" class="hr-btn hr-btn-sm hr-btn-danger" title="Delete" @click="confirmDeleteDocument(document)">
                          <i class="bx bx-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="documents.length === 0">
                    <td colspan="9" class="hr-text-center hr-py-4 hr-text-muted">No documents found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </HrPageLayout>

    <!-- Add Document Modal -->
    <ModalWrapper ref="addDocumentModal" title="Add New Document" size="large">
      <template #body>
        <form id="document-form" @submit.prevent="submitDocument" class="hr-form-grid">
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Document Name <span class="hr-text-danger">*</span></label>
            <input type="text" class="hr-input" v-model="documentForm.name" placeholder="Enter document name" required>
            <small class="hr-text-muted hr-d-block hr-mt-1">Provide a descriptive name for easy identification</small>
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Description</label>
            <textarea class="hr-textarea" v-model="documentForm.description" rows="3" placeholder="Enter document description"></textarea>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Department</label>
            <select class="hr-select" v-model="documentForm.departmentId">
              <option value="">General Document</option>
              <option v-for="department in departments" :key="department.id" :value="department.id">
                {{ department.departmentName }}
              </option>
            </select>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Document type / Category <span class="hr-text-danger">*</span></label>
            <input
              v-model="documentForm.documentType"
              type="text"
              class="hr-input"
              list="document-type-datalist"
              placeholder="Pick a suggestion or type your own"
              required
            >
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Upload Document <span class="hr-text-danger">*</span></label>
            <input type="file" id="document-file" class="hr-input" @change="handleFileUpload" accept=".pdf,.zip,application/pdf,application/zip,application/x-zip-compressed" required>
            <small class="hr-text-muted hr-d-block hr-mt-1">Accepted formats: PDF, ZIP</small>
            <div v-if="documentForm.file" class="hr-mt-2">
              <span class="hr-badge hr-badge-success">
                <i class="bx bx-check"></i> {{ documentForm.file.name }} ({{ formatFileSize(documentForm.file.size) }})
              </span>
            </div>
          </div>
        </form>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.addDocumentModal?.close()">Cancel</button>
        <button type="submit" form="document-form" class="hr-btn hr-btn-success" :disabled="isSubmitting" @click="submitDocument">
          <span v-if="isSubmitting" class="hr-spinner small"></span>
          <i v-else class="bx bx-save"></i> Save Document
        </button>
      </template>
    </ModalWrapper>

    <!-- Edit Document Modal -->
    <ModalWrapper ref="editDocumentModal" title="Edit Document" size="medium">
      <template #body>
        <form id="edit-document-form" @submit.prevent="updateDocument" class="hr-form-grid">
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Document Name <span class="hr-text-danger">*</span></label>
            <input type="text" class="hr-input" v-model="editDocumentForm.name" placeholder="Enter document name" required>
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Description</label>
            <textarea class="hr-textarea" v-model="editDocumentForm.description" placeholder="Enter document description"></textarea>
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Department</label>
            <select class="hr-select" v-model="editDocumentForm.departmentId">
              <option value="">General Document</option>
              <option v-for="department in departments" :key="department.id" :value="department.id">
                {{ department.departmentName }}
              </option>
            </select>
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Document type / Category</label>
            <input
              v-model="editDocumentForm.documentType"
              type="text"
              class="hr-input"
              list="document-type-datalist"
              placeholder="Pick a suggestion or type your own"
            >
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Upload New File (Optional)</label>
            <input type="file" id="edit-document-file" class="hr-input" @change="handleEditFileUpload">
          </div>
        </form>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.editDocumentModal?.close()">Cancel</button>
        <button type="submit" form="edit-document-form" class="hr-btn hr-btn-primary" :disabled="isSubmitting" @click="updateDocument">
          <span v-if="isSubmitting" class="hr-spinner small"></span>
          <i v-else class="bx bx-check"></i> Save Changes
        </button>
      </template>
    </ModalWrapper>

    <!-- Delete Confirmation Modal -->
    <ModalWrapper ref="deleteDocumentModal" title="Confirm Delete" size="small">
      <template #body>
        <p>Are you sure you want to delete the document "{{ deleteDocumentName }}"?</p>
        <p class="hr-text-danger hr-mt-2">This action cannot be undone.</p>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.deleteDocumentModal?.close()">Cancel</button>
        <button type="button" class="hr-btn hr-btn-danger" @click="deleteDocument" :disabled="isDeleting">
          <span v-if="isDeleting" class="hr-spinner small"></span>
          <i v-else class="bx bx-trash"></i> Delete
        </button>
      </template>
    </ModalWrapper>

    <!-- Send document by email -->
    <ModalWrapper ref="sendDocumentEmailModal" title="Send document by email" size="medium">
      <template #body>
        <p v-if="emailModalDoc" class="hr-mb-3">
          <strong>{{ emailModalDoc.name }}</strong>
        </p>
        <div class="email-mode-row hr-mb-3">
          <label class="email-mode-option">
            <input v-model="emailMode" type="radio" value="one" class="hr-mr-1">
            One employee
          </label>
          <label class="email-mode-option">
            <input v-model="emailMode" type="radio" value="all" class="hr-mr-1">
            All active employees (with email on file)
          </label>
        </div>

        <template v-if="emailMode === 'one'">
          <div class="hr-filter-group hr-mb-2">
            <label class="hr-form-label">Find employee</label>
            <div class="hr-flex-between" style="gap: 8px;">
              <input v-model="emailSearchKeyword" type="text" class="hr-input" placeholder="Name, ID, email, PF no…" @keyup.enter="searchEmployeesForEmail">
              <button type="button" class="hr-btn hr-btn-primary" :disabled="emailSearchLoading" @click="searchEmployeesForEmail">
                {{ emailSearchLoading ? "…" : "Search" }}
              </button>
            </div>
          </div>
          <div v-if="selectedEmployeeForEmail" class="hr-mb-3 hr-p-2" style="background: #f1f5f9; border-radius: 8px;">
            <strong>Selected:</strong> {{ fullEmployeeName(selectedEmployeeForEmail) }}
            <span class="hr-text-muted"> — {{ selectedEmployeeForEmail.email || "no email" }}</span>
          </div>
          <div class="email-search-results" style="max-height: 220px; overflow: auto;">
            <table v-if="emailSearchResults.length" class="hr-table hr-table-sm">
              <thead><tr><th>Name</th><th>Email</th><th></th></tr></thead>
              <tbody>
                <tr v-for="emp in emailSearchResults" :key="'em-' + emp.id">
                  <td>{{ fullEmployeeName(emp) }}</td>
                  <td class="hr-text-muted">{{ emp.email || "—" }}</td>
                  <td>
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="pickEmployeeForEmail(emp)">Select</button>
                  </td>
                </tr>
              </tbody>
            </table>
            <p v-else class="hr-text-muted hr-small">Search and select an employee. The file will be emailed to their work email on file.</p>
          </div>
        </template>

        <div v-else class="hr-p-3 hr-mb-2 email-all-notice">
          <p class="hr-mb-2"><strong>Send to everyone</strong></p>
          <p class="hr-small hr-text-muted hr-mb-0">
            Sends this document as an attachment to every employee with <code>status = Active</code> and a non-empty email in the register.
            This can take several minutes for large staff lists and may hit mail rate limits.
          </p>
        </div>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.sendDocumentEmailModal?.close()">Cancel</button>
        <button
          v-if="emailMode === 'one'"
          type="button"
          class="hr-btn hr-btn-success"
          :disabled="sendingEmail || !selectedEmployeeForEmail"
          @click="sendDocumentToEmployeeEmail"
        >
          {{ sendingEmail ? "Sending…" : "Send email" }}
        </button>
        <button
          v-else
          type="button"
          class="hr-btn hr-btn-warning"
          :disabled="sendingEmail"
          @click="sendDocumentToAllEmployeesEmail"
        >
          {{ sendingEmail ? "Sending…" : "Send to all employees" }}
        </button>
      </template>
    </ModalWrapper>

    <!-- Document Viewer Modal -->
    <ModalWrapper ref="documentViewerModal" :title="viewingDocument ? viewingDocument.name : 'Document Viewer'" size="large">
      <template #body>
        <div v-if="isLoadingPreview" class="hr-loading-wrap" style="min-height: 400px;">
          <div class="hr-spinner"></div>
          <p class="hr-text-muted hr-mt-2">Loading document preview...</p>
        </div>
        <div v-else-if="previewError" class="hr-text-center hr-py-5">
          <i class="bx bx-error-circle" style="font-size: 48px; color: #ef4444;"></i>
          <p class="hr-text-danger hr-mt-2">{{ previewError }}</p>
          <button class="hr-btn hr-btn-primary hr-mt-3" @click="downloadDocument(viewingDocument)">
            <i class="bx bx-download"></i> Download instead
          </button>
        </div>
        <div v-else-if="viewingDocument" class="doc-viewer-body">
          <div v-if="viewingDocument.filePath && viewingDocument.filePath.toLowerCase().endsWith('.pdf')" class="doc-iframe-wrap">
            <iframe :src="(viewingDocument.previewUrl || '') + '?embed=true'" class="doc-iframe" allowfullscreen></iframe>
          </div>
          <div v-else-if="isImageFile(viewingDocument.filePath)" class="hr-text-center hr-p-3">
            <img :src="viewingDocument.previewUrl" alt="Document" class="doc-preview-img">
          </div>
          <div v-else-if="isTextFile(viewingDocument.filePath)" class="doc-iframe-wrap">
            <iframe :src="(viewingDocument.previewUrl || '') + '?embed=true'" class="doc-iframe"></iframe>
          </div>
          <div v-else class="hr-text-center hr-py-5">
            <i class="bx bx-file" style="font-size: 48px; color: #94a3b8;"></i>
            <h5 class="hr-mt-3">{{ viewingDocument.name }}</h5>
            <p class="hr-text-muted hr-mb-3">
              {{ getFileExtension(viewingDocument.filePath) ? getFileExtension(viewingDocument.filePath).toUpperCase() : 'Unknown' }} •
              {{ viewingDocument.departmentName || 'General' }} • Uploaded on {{ formatDate(viewingDocument.createdAt) }}
            </p>
            <p class="hr-text-muted">This document type cannot be previewed directly.</p>
            <button class="hr-btn hr-btn-primary hr-mt-3" @click="downloadDocument(viewingDocument)">
              <i class="bx bx-download"></i> Download
            </button>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="hr-flex-between hr-w-100">
          <div>
            <span class="hr-badge hr-badge-info hr-mr-2">
              <i class="bx bx-show"></i> {{ viewingDocument ? (viewingDocument.views || '0') : '0' }} views
            </span>
            <span v-if="viewingDocument" class="hr-badge" :class="getCategoryBadgeClass(viewingDocument.category)">
              {{ viewingDocument.category || 'Document' }}
            </span>
          </div>
          <div>
            <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.documentViewerModal?.close()">Close</button>
            <button type="button" class="hr-btn hr-btn-warning" @click="openEditFromViewer">
              <i class="bx bx-edit"></i> Edit
            </button>
          </div>
        </div>
      </template>
    </ModalWrapper>
  </div>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { authUrl, execute, executeDownload } from "@/api";
import moment from "moment";

export default {
  name: "DocumentsGeneral",
  components: { HrPageLayout, ModalWrapper },
  data() {
    return {
      documents: [],
      departments: [],
      isLoading: false,
      isSubmitting: false,
      isDeleting: false,
      message: '',
      toastKind: 'success',
      emailMode: 'one',
      searchKeyword: '',
      selectedDepartment: '',
      selectedCategory: '',
      selectedDocumentType: '',
      documentTypeSuggestions: [],
      statisticsEnabled: true,
      searchTimeout: null,
      viewingDocument: null,
      isLoadingPreview: false,
      previewError: null,
      documentForm: {
        name: '',
        description: '',
        departmentId: '',
        category: '',
        documentType: '',
        file: null
      },
      editDocumentForm: {
        id: null,
        name: '',
        description: '',
        departmentId: '',
        category: '',
        documentType: '',
        file: null,
        currentFilePath: ''
      },
      emailModalDoc: null,
      emailSearchKeyword: '',
      emailSearchLoading: false,
      emailSearchResults: [],
      selectedEmployeeForEmail: null,
      sendingEmail: false,
      deleteDocumentId: null,
      deleteDocumentName: '',
      statistics: {
        totalDocuments: 0,
        recentUploads: 0,
        mostViewed: 0,
        categories: 0
      }
    };
  },
  mounted() {
    this.getDocuments();
    this.getDepartments();
    this.loadDocumentTypeSuggestions();
  },
  computed: {
    toastHeading() {
      if (this.toastKind === "success") return "Success";
      if (this.toastKind === "info") return "Notice";
      return "Error";
    }
  },
  methods: {
    setToast(text, kind = "error") {
      this.message = text || "";
      this.toastKind = kind === "success" || kind === "info" || kind === "error" ? kind : "error";
    },
    clearToast() {
      this.message = "";
      this.toastKind = "success";
    },
    authUrl() {
      return authUrl;
    },
    getCategoryBadgeClass(category) {
      const map = {
        Policy: 'hr-badge-success',
        Procedure: 'hr-badge-primary',
        Form: 'hr-badge-info',
        Report: 'hr-badge-warning',
        Manual: 'hr-badge-secondary',
        Other: 'hr-badge-dark'
      };
      return map[category] || 'hr-badge-dark';
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    openAddModal() {
      this.resetDocumentForm();
      this.$refs.addDocumentModal?.open();
    },
    openEditFromViewer() {
      if (this.viewingDocument) {
        this.$refs.documentViewerModal?.close();
        this.editDocument(this.viewingDocument);
      }
    },
    getDocuments() {
      this.isLoading = true;
      const data = new FormData();
      data.append("function", "getDocuments");
      if (this.selectedDepartment) data.append("department", this.selectedDepartment);
      if (this.selectedCategory) data.append("category", this.selectedCategory);
      if (this.selectedDocumentType) data.append("documentType", this.selectedDocumentType);
      if (this.searchKeyword) data.append("keyword", this.searchKeyword);
      execute(data, authUrl)
        .then((res) => {
          this.isLoading = false;
          if (res.data.success && res.data.data?.documents) {
            this.documents = res.data.data.documents.map(doc => ({
              ...doc,
              fileUrl: doc.filePath ? authUrl + doc.filePath : '#',
              uploadDate: doc.createdAt,
              uploadedBy: doc.uploadedBy || 'System Admin',
              department: doc.departmentName || 'General'
            }));
            this.updateStatisticsFromResponse(res);
          } else {
            this.documents = [];
          }
        })
        .catch((e) => {
          this.isLoading = false;
          this.setToast(e.message || "Error fetching documents", "error");
          this.documents = [];
        });
    },
    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
        .then((res) => {
          if (res.data.success && res.data.data?.departments) {
            this.departments = res.data.data.departments;
          }
        })
        .catch(() => {});
    },
    loadDocumentTypeSuggestions() {
      const data = new FormData();
      data.append("function", "getDirectoryDocumentTypeSuggestions");
      execute(data, authUrl)
        .then((res) => {
          if (res.data.success && res.data.data?.types) {
            this.documentTypeSuggestions = res.data.data.types;
          }
        })
        .catch(() => {
          this.documentTypeSuggestions = [];
        });
    },
    updateStatisticsFromResponse(response) {
      if (response?.data?.data?.metrics) {
        const m = response.data.data.metrics;
        this.statistics = {
          totalDocuments: parseInt(m.totalDocuments) || 0,
          recentUploads: m.recentUploads?.length || 0,
          mostViewed: m.mostViewed?.length || 0,
          categories: m.categories?.length || 0
        };
      }
    },
    handleDepartmentChange() { this.getDocuments(); },
    handleCategoryChange() { this.getDocuments(); },
    handleDocumentTypeFilterChange() { this.getDocuments(); },
    handleSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.getDocuments(), 500);
    },
    handleFileUpload(e) { this.documentForm.file = e.target.files[0]; },
    handleEditFileUpload(e) { this.editDocumentForm.file = e.target.files[0]; },
    resetDocumentForm() {
      this.documentForm = { name: '', description: '', departmentId: '', category: '', documentType: '', file: null };
      const el = document.getElementById('document-file');
      if (el) el.value = '';
    },
    submitDocument() {
      if (!this.documentForm.name || !this.documentForm.file) {
        this.setToast("Please fill all required fields", "error");
        return;
      }
      this.isSubmitting = true;
      const data = new FormData();
      data.append("function", "addDocument");
      data.append("name", this.documentForm.name);
      data.append("description", this.documentForm.description || '');
      data.append("departmentId", this.documentForm.departmentId || '');
      const docType = (this.documentForm.documentType || '').trim();
      data.append("documentType", docType);
      data.append("category", docType);
      data.append("file", this.documentForm.file);
      execute(data, authUrl)
        .then((res) => {
          this.isSubmitting = false;
          if (res.data.success) {
            this.$refs.addDocumentModal?.close();
            this.resetDocumentForm();
            this.setToast(res.data.message || "Document added successfully", "success");
            this.getDocuments();
            this.loadDocumentTypeSuggestions();
          } else {
            this.setToast(res.data.message || "Failed to add document", "error");
          }
        })
        .catch((e) => {
          this.isSubmitting = false;
          this.setToast(e.message || "Error adding document", "error");
        });
    },
    editDocument(document) {
      this.editDocumentForm = {
        id: document.id,
        name: document.name,
        description: document.description || '',
        departmentId: document.departmentId || '',
        category: document.document_type || document.documentType || document.category || '',
        documentType: document.document_type || document.documentType || '',
        file: null,
        currentFilePath: document.filePath || ''
      };
      const el = document.getElementById('edit-document-file');
      if (el) el.value = '';
      this.$refs.editDocumentModal?.open();
    },
    updateDocument() {
      if (!this.editDocumentForm.name) {
        this.setToast("Document name is required", "error");
        return;
      }
      this.isSubmitting = true;
      const data = new FormData();
      data.append("function", "updateDocument");
      data.append("id", this.editDocumentForm.id);
      data.append("name", this.editDocumentForm.name);
      data.append("description", this.editDocumentForm.description || '');
      data.append("departmentId", this.editDocumentForm.departmentId || '');
      const docType = (this.editDocumentForm.documentType || '').trim();
      data.append("documentType", docType);
      data.append("category", docType);
      data.append("currentFilePath", this.editDocumentForm.currentFilePath || '');
      data.append("replaceFile", this.editDocumentForm.file ? "1" : "0");
      if (this.editDocumentForm.file) data.append("file", this.editDocumentForm.file);
      execute(data, authUrl)
        .then((res) => {
          this.isSubmitting = false;
          if (res.data.success) {
            this.$refs.editDocumentModal?.close();
            this.$refs.documentViewerModal?.close();
            this.setToast(res.data.message || "Document updated successfully", "success");
            this.getDocuments();
            this.loadDocumentTypeSuggestions();
          } else {
            this.setToast(res.data.message || "Failed to update document", "error");
          }
        })
        .catch((e) => {
          this.isSubmitting = false;
          this.setToast(e.message || "Error updating document", "error");
        });
    },
    confirmDeleteDocument(document) {
      this.deleteDocumentId = document.id;
      this.deleteDocumentName = document.name;
      this.$refs.deleteDocumentModal?.open();
    },
    deleteDocument() {
      if (!this.deleteDocumentId) return;
      this.isDeleting = true;
      const data = new FormData();
      data.append("function", "deleteDocument");
      data.append("id", this.deleteDocumentId);
      execute(data, authUrl)
        .then((res) => {
          this.isDeleting = false;
          if (res.data.success) {
            this.$refs.deleteDocumentModal?.close();
            this.setToast(res.data.message || "Document deleted successfully", "success");
            this.getDocuments();
            this.deleteDocumentId = null;
            this.deleteDocumentName = '';
          } else {
            this.setToast(res.data.message || "Failed to delete document", "error");
          }
        })
        .catch((e) => {
          this.isDeleting = false;
          this.setToast(e.message || "Error deleting document", "error");
        });
    },
    exportReport() {
      const data = new FormData();
      data.append("function", "exportDocumentsReport");
      if (this.selectedDepartment) data.append("department", this.selectedDepartment);
      if (this.selectedCategory) data.append("category", this.selectedCategory);
      executeDownload(data, authUrl)
        .then((response) => {
          this.setToast("Report downloaded successfully", "success");
          const link = window.document.createElement("a");
          link.href = URL.createObjectURL(response.data);
          link.download = "Documents_Report.csv";
          window.document.body.appendChild(link);
          link.click();
          window.document.body.removeChild(link);
        })
        .catch((e) => {
          this.setToast(e.message || "Report download failed", "error");
        });
    },
    viewDocument(document) {
      this.viewingDocument = { ...document };
      this.isLoadingPreview = true;
      this.previewError = null;
      this.$refs.documentViewerModal?.open();
      const data = new FormData();
      data.append("function", "incrementDocumentViews");
      data.append("id", document.id);
      execute(data, authUrl).catch(() => {});
      setTimeout(() => {
        this.isLoadingPreview = false;
        if (!document.filePath) {
          this.previewError = "Document file not found";
          return;
        }
        this.viewingDocument = { ...document, previewUrl: authUrl + document.filePath };
        const idx = this.documents.findIndex(d => d.id === document.id);
        if (idx !== -1) {
          this.documents[idx].views = (parseInt(this.documents[idx].views || 0) + 1).toString();
          this.viewingDocument.views = this.documents[idx].views;
        }
      }, 300);
    },
    async downloadDocument(doc) {
      if (!doc?.id) return;
      try {
        const data = new FormData();
        data.append("function", "downloadDirectoryDocument");
        data.append("id", String(doc.id));
        const response = await executeDownload(data, authUrl);
        const blob = response.data;
        const a = window.document.createElement("a");
        const ext = this.getFileExtension(doc.filePath);
        let fname = (doc.name || "document").replace(/[/\\?%*:|"<>]/g, "_");
        if (ext && !fname.toLowerCase().endsWith("." + ext.toLowerCase())) {
          fname += "." + ext;
        }
        a.href = URL.createObjectURL(blob);
        a.download = fname;
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
        this.setToast("Download started", "success");
      } catch (e) {
        this.setToast(e.message || "Download failed", "error");
      }
    },
    openSendEmailModal(doc) {
      this.emailModalDoc = doc;
      this.emailMode = "one";
      this.emailSearchKeyword = "";
      this.emailSearchResults = [];
      this.selectedEmployeeForEmail = null;
      this.$refs.sendDocumentEmailModal?.open();
    },
    async searchEmployeesForEmail() {
      const kw = (this.emailSearchKeyword || "").trim();
      if (kw.length < 2) {
        this.setToast("Type at least 2 characters to search employees", "info");
        return;
      }
      this.emailSearchLoading = true;
      this.emailSearchResults = [];
      try {
        const data = new FormData();
        data.append("function", "getEmployees");
        data.append("page", "1");
        data.append("rows_per_page", "40");
        data.append("keyword", kw);
        const res = await execute(data, authUrl);
        if (res.data?.success && res.data.data?.employees) {
          this.emailSearchResults = res.data.data.employees;
        }
      } catch (_) {
        this.emailSearchResults = [];
      } finally {
        this.emailSearchLoading = false;
      }
    },
    pickEmployeeForEmail(emp) {
      this.selectedEmployeeForEmail = emp;
    },
    fullEmployeeName(emp) {
      if (!emp) return "";
      const p = [emp.firstName, emp.middleName, emp.lastName].filter(Boolean);
      return p.join(" ").trim() || emp.names || "—";
    },
    async sendDocumentToEmployeeEmail() {
      if (!this.emailModalDoc?.id || !this.selectedEmployeeForEmail?.id) {
        this.setToast("Select an employee first", "info");
        return;
      }
      this.sendingEmail = true;
      try {
        const data = new FormData();
        data.append("function", "sendDirectoryDocumentToEmployee");
        data.append("documentId", String(this.emailModalDoc.id));
        data.append("employeeId", String(this.selectedEmployeeForEmail.id));
        const res = await execute(data, authUrl);
        if (res.data?.success) {
          this.setToast(res.data.message || "Email sent", "success");
          this.$refs.sendDocumentEmailModal?.close();
        } else {
          this.setToast(res.data?.message || "Could not send email", "error");
        }
      } catch (e) {
        this.setToast(e.message || "Could not send email", "error");
      } finally {
        this.sendingEmail = false;
      }
    },
    async sendDocumentToAllEmployeesEmail() {
      if (!this.emailModalDoc?.id) return;
      const ok = window.confirm(
        "Send this document to every active employee who has an email on file? This may take several minutes."
      );
      if (!ok) return;
      this.sendingEmail = true;
      try {
        const data = new FormData();
        data.append("function", "sendDirectoryDocumentToAllEmployees");
        data.append("documentId", String(this.emailModalDoc.id));
        const res = await execute(data, authUrl);
        const d = res.data?.data || {};
        if (res.data?.success) {
          const detail =
            typeof d.sent === "number"
              ? ` Sent: ${d.sent}. Failed: ${d.failed || 0}. Skipped invalid: ${d.skippedInvalid || 0}.`
              : "";
          this.setToast((res.data.message || "Bulk email completed.") + detail, "success");
          this.$refs.sendDocumentEmailModal?.close();
        } else {
          this.setToast(res.data?.message || "Bulk email failed", "error");
        }
      } catch (e) {
        this.setToast(e.message || "Bulk email failed", "error");
      } finally {
        this.sendingEmail = false;
      }
    },
    getFileExtension(filePath) {
      if (!filePath) return '';
      const parts = filePath.split('.');
      return parts.length > 1 ? parts[parts.length - 1] : '';
    },
    isImageFile(filePath) {
      if (!filePath) return false;
      const ext = filePath.toLowerCase();
      return ext.endsWith('.jpg') || ext.endsWith('.jpeg') || ext.endsWith('.png') || ext.endsWith('.gif');
    },
    isTextFile(filePath) {
      if (!filePath) return false;
      const ext = filePath.toLowerCase();
      return ext.endsWith('.txt') || ext.endsWith('.html') || ext.endsWith('.htm');
    },
    formatDate(dateString) {
      return dateString ? moment(dateString).format('DD MMM, YYYY') : '—';
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.doc-toast {
  position: fixed;
  bottom: 20px;
  right: 20px;
  padding: 14px 20px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  z-index: 1100;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 400px;
  animation: slideIn 0.3s ease;
}
.doc-toast.success { background: linear-gradient(135deg, #d1fae5 0%, #ecfdf5 100%); border-left: 4px solid #10b981; }
.doc-toast.error { background: linear-gradient(135deg, #fee2e2 0%, #fef2f2 100%); border-left: 4px solid #ef4444; }
.doc-toast.info { background: linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%); border-left: 4px solid #3b82f6; }
.email-mode-row { display: flex; flex-wrap: wrap; gap: 16px; align-items: center; }
.email-mode-option { display: inline-flex; align-items: center; cursor: pointer; font-weight: 500; }
.email-all-notice { background: #fffbeb; border: 1px solid #fcd34d; border-radius: 8px; }
.email-all-notice code { font-size: 0.85em; }
.doc-toast-close { background: none; border: none; font-size: 20px; cursor: pointer; padding: 0 4px; }
.doc-filters { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.doc-desc-cell { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.doc-viewer-body { min-height: 400px; }
.doc-iframe-wrap { width: 100%; height: 70vh; border-radius: 8px; overflow: hidden; }
.doc-iframe { width: 100%; height: 100%; border: none; }
.doc-preview-img { max-height: 70vh; max-width: 100%; object-fit: contain; }
.hr-flex-between { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; }
.hr-w-100 { width: 100%; }
.hr-mr-2 { margin-right: 8px; }
.doc-actions { display: flex; flex-wrap: wrap; gap: 4px; align-items: center; }
.hr-mb-2 { margin-bottom: 8px; }
.hr-mb-3 { margin-bottom: 12px; }
.hr-p-2 { padding: 8px; }
.hr-small { font-size: 0.875rem; }
</style>
